import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import propertyService from '../../services/propertyService';
import './AdminDashboard.css';

const EditPropertyPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [neighborhoods, setNeighborhoods] = useState([]);
  
  const [propertyData, setPropertyData] = useState({
    address: '',
    csz: '', // NEW: City, State, ZIP field
    neighborhood: '',
    price: '',
    bedrooms: '',
    bathrooms: '',
    sqft: '',
    lotSize: '',
    description: '',
    propertyType: 'House',
    listingType: 'purchase',
    yearBuilt: '',
    yearRenovated: '',
    latitude: '',
    longitude: '',
    propertyStatus: 'For Sale',
    daysOnMarket: '0',
    hoaFees: '0',
    propertyTax: '0',
    features: '',
    isTrending: false, // NEW: Trending property toggle
    agentName: '',
    agency: '',
    contactPhone: '',
    contactEmail: '',
    isAvailable: true,
    allowsDogs: false,
    allowsCats: false,
    petDeposit: '0',
    petRent: '0',
    parkingType: 'None',
    parkingSpaces: '0',
    laundryType: 'None',
    garageType: 'None',
    garageSpaces: '0',
  });

  useEffect(() => {
    fetchProperty();
    fetchNeighborhoods();
  }, [id]);

  const fetchProperty = async () => {
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      const response = await propertyService.getPropertyById(id);
      
      if (response && response.data) {
        const property = response.data;
        
        console.log('Property loaded:', property);
        console.log('Neighborhood ID:', property.neighborhoodId);
        console.log('Neighborhood (string):', property.neighborhood);
        
        // Extract the neighborhood ID properly
        let neighborhoodValue = '';
        if (property.neighborhoodId) {
          // If neighborhoodId is an object with _id, use that
          if (typeof property.neighborhoodId === 'object' && property.neighborhoodId._id) {
            neighborhoodValue = property.neighborhoodId._id;
          } else {
            // Otherwise it's already a string ID
            neighborhoodValue = property.neighborhoodId.toString();
          }
        } else if (property.neighborhood) {
          neighborhoodValue = property.neighborhood;
        }
        
        console.log('Neighborhood value to set:', neighborhoodValue);
        
        // Populate form with existing property data
        setPropertyData({
          address: property.address || '',
          csz: property.csz || '',
          neighborhood: neighborhoodValue,
          price: property.price || '',
          bedrooms: property.bedrooms || '',
          bathrooms: property.bathrooms || '',
          sqft: property.sqft || '',
          lotSize: property.lotSize || '',
          description: property.description || '',
          propertyType: property.propertyType || 'House',
          listingType: property.listingType || 'purchase',
          yearBuilt: property.yearBuilt || '',
          yearRenovated: property.yearRenovated || '',
          latitude: property.location?.coordinates?.[1] || '',
          longitude: property.location?.coordinates?.[0] || '',
          propertyStatus: property.propertyStatus || 'For Sale',
          daysOnMarket: property.daysOnMarket || '0',
          hoaFees: property.hoaFees || '0',
          propertyTax: property.propertyTax || '0',
          features: Array.isArray(property.features) ? property.features.join(', ') : '',
          isTrending: property.isTrending || false,
          agentName: property.listedBy?.agentName || '',
          agency: property.listedBy?.agency || '',
          contactPhone: property.listedBy?.contactPhone || '',
          contactEmail: property.listedBy?.contactEmail || '',
          isAvailable: property.isAvailable !== undefined ? property.isAvailable : true,
          allowsDogs: property.petPolicy?.allowsDogs || false,
          allowsCats: property.petPolicy?.allowsCats || false,
          petDeposit: property.petPolicy?.petDeposit || '0',
          petRent: property.petPolicy?.petRent || '0',
          parkingType: property.parking?.type || 'None',
          parkingSpaces: property.parking?.spaces || '0',
          laundryType: property.laundry?.type || 'None',
          garageType: property.garage?.type || 'None',
          garageSpaces: property.garage?.spaces || '0',
        });
      }
    } catch (error) {
      console.error('Error fetching property:', error);
      setErrorMessage('Failed to load property details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNeighborhoods = async () => {
    try {
      const response = await propertyService.getNeighborhoods();
      if (response && response.data) {
        console.log('Neighborhoods loaded:', response.data.length);
        console.log('First neighborhood:', response.data[0]);
        console.log('Sample neighborhood IDs:', response.data.slice(0, 3).map(n => ({ name: n.name, id: n._id })));
        setNeighborhoods(response.data);
      }
    } catch (error) {
      console.error('Error fetching neighborhoods:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPropertyData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      // Prepare data for backend
      const updateData = {
        address: propertyData.address,
        csz: propertyData.csz,
        price: propertyData.price,
        numericPrice: parseFloat(propertyData.price.replace(/[^0-9.]/g, '')),
        bedrooms: parseInt(propertyData.bedrooms),
        bathrooms: parseFloat(propertyData.bathrooms),
        sqft: parseInt(propertyData.sqft),
        lotSize: parseFloat(propertyData.lotSize) || 0,
        description: propertyData.description,
        propertyType: propertyData.propertyType,
        listingType: propertyData.listingType,
        yearBuilt: propertyData.yearBuilt ? parseInt(propertyData.yearBuilt) : undefined,
        yearRenovated: propertyData.yearRenovated ? parseInt(propertyData.yearRenovated) : undefined,
        propertyStatus: propertyData.propertyStatus,
        daysOnMarket: parseInt(propertyData.daysOnMarket) || 0,
        hoaFees: parseFloat(propertyData.hoaFees) || 0,
        propertyTax: parseFloat(propertyData.propertyTax) || 0,
        features: propertyData.features ? propertyData.features.split(',').map(f => f.trim()).filter(f => f) : [],
        isTrending: propertyData.isTrending, // Include trending status
        listedBy: {
          agentName: propertyData.agentName,
          agency: propertyData.agency,
          contactPhone: propertyData.contactPhone,
          contactEmail: propertyData.contactEmail,
        },
        isAvailable: propertyData.isAvailable,
        petPolicy: {
          allowsDogs: propertyData.allowsDogs,
          allowsCats: propertyData.allowsCats,
          petDeposit: parseFloat(propertyData.petDeposit) || 0,
          petRent: parseFloat(propertyData.petRent) || 0,
        },
        parking: {
          type: propertyData.parkingType,
          spaces: parseInt(propertyData.parkingSpaces) || 0,
        },
        laundry: {
          type: propertyData.laundryType,
        },
        garage: {
          type: propertyData.garageType,
          spaces: parseInt(propertyData.garageSpaces) || 0,
        },
      };

      // Only include neighborhood if one is selected (to avoid overwriting with empty value)
      if (propertyData.neighborhood) {
        updateData.neighborhood = propertyData.neighborhood;
      }

      // Update location if coordinates provided
      if (propertyData.latitude && propertyData.longitude) {
        updateData.location = {
          type: 'Point',
          coordinates: [parseFloat(propertyData.longitude), parseFloat(propertyData.latitude)]
        };
      }

      const response = await propertyService.updateProperty(id, updateData);
      
      if (response && response.success) {
        setSuccessMessage('Property updated successfully!');
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error updating property:', error);
      setErrorMessage(`Failed to update property: ${error.message || 'Unknown error'}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  if (isLoading) {
    return (
      <div className="admin-container">
        <div className="text-center p-6">
          <div className="loading-spinner"></div>
          <p className="mt-3">Loading property...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ color: '#ffffff' }}>
      <style>{`
        .content-container {
          background-color: transparent !important;
        }
        .alert-success {
          background-color: rgba(4, 120, 87, 0.15) !important;
          color: #4ade80 !important;
          border: 1px solid rgba(4, 120, 87, 0.3) !important;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .alert-danger {
          background-color: rgba(220, 38, 38, 0.15) !important;
          color: #fca5a5 !important;
          border: 1px solid rgba(220, 38, 38, 0.3) !important;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .form-section {
          background-color: rgba(26, 26, 26, 0.6) !important;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }
        .form-section-header {
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          background-color: transparent !important;
        }
        .form-section-title {
          color: #ffffff !important;
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
          background-color: transparent !important;
          text-align: left;
        }
        .form-section-body {
          padding-top: 4px;
          background-color: transparent !important;
        }
        .form-label {
          color: rgba(255, 255, 255, 0.8) !important;
          font-weight: 500;
          margin-bottom: 8px;
          display: block;
        }
        .form-label-required::after {
          content: " *";
          color: #f87171;
        }
        .form-control {
          background-color: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          color: #ffffff !important;
          padding: 10px 12px;
          border-radius: 6px;
          transition: all 0.2s;
          width: 100%;
        }
        .form-control:focus {
          border-color: rgba(255, 255, 255, 0.4) !important;
          outline: none;
        }
        .form-control::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }
        .form-row {
          display: grid;
          gap: 20px;
          margin-bottom: 20px;
        }
        .form-row-2 {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin-bottom: 20px;
        }
        .form-row-3 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-bottom: 20px;
        }
        .form-row-4 {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 20px;
        }
        .form-group {
          display: flex;
          flex-direction: column;
        }
        .form-checkbox {
          width: 18px;
          height: 18px;
          margin-right: 8px;
        }
        textarea.form-control {
          min-height: 100px;
          resize: vertical;
          color: #ffffff !important;
        }
        textarea.form-control::placeholder {
          color: rgba(255, 255, 255, 0.4) !important;
        }
        textarea {
          color: #ffffff !important;
        }
        .btn-primary {
          background-color: transparent !important;
          color: #ffffff !important;
          border: 1px solid rgba(255, 255, 255, 0.3) !important;
          padding: 12px 32px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1rem;
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
        }
        .btn-primary:hover:not(:disabled) {
          background-color: rgba(255, 255, 255, 0.1) !important;
          border-color: rgba(255, 255, 255, 0.5) !important;
          box-shadow: 0 0 30px rgba(255, 255, 255, 0.2);
        }
        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .btn-secondary {
          background-color: transparent !important;
          color: #ffffff !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          padding: 12px 32px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 0 15px rgba(255, 255, 255, 0.05);
        }
        .btn-secondary:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
          border-color: rgba(255, 255, 255, 0.4) !important;
        }
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 32px;
          padding-top: 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }
        .loading-spinner {
          border: 3px solid rgba(255, 255, 255, 0.1) !important;
          border-top-color: rgba(255, 255, 255, 0.8) !important;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          display: inline-block;
          margin-right: 8px;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .text-xs {
          font-size: 0.75rem;
        }
        .text-sm {
          font-size: 0.875rem;
        }
        .text-text-light {
          color: rgba(255, 255, 255, 0.5);
        }
        .mt-1 {
          margin-top: 0.25rem;
        }
        .mt-2 {
          margin-top: 0.5rem;
        }
        .mb-2 {
          margin-bottom: 0.5rem;
        }
        .mb-4 {
          margin-bottom: 1rem;
        }
        .flex {
          display: flex;
        }
        .items-center {
          align-items: center;
        }
        .ml-2 {
          margin-left: 0.5rem;
        }
        .toggle-switch-container {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 52px;
          height: 28px;
        }
        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(255, 255, 255, 0.2);
          transition: 0.3s;
          border-radius: 28px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 4px;
          bottom: 4px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle-switch input:checked + .toggle-slider {
          background-color: #4f46e5;
        }
        .toggle-switch input:checked + .toggle-slider:before {
          transform: translateX(24px);
        }
        .toggle-label {
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.9rem;
        }
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
        }
        @media (max-width: 768px) {
          .form-row-2,
          .form-row-3,
          .form-row-4 {
            grid-template-columns: 1fr;
          }
          .page-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }
        }
      `}</style>
      
      <div className="content-container">
        <div className="page-header">
          <button 
            onClick={handleCancel}
            className="btn-secondary"
          >
            ← Back to Dashboard
          </button>
        </div>

        {successMessage && (
          <div className="alert-success mb-4">
            {successMessage}
          </div>
        )}
        
        {errorMessage && (
          <div className="alert-danger mb-4">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSave}>
        {/* Basic Information */}
        <div className="form-section">
          <div className="form-section-header">
            <h3 className="form-section-title">Basic Information</h3>
          </div>
          <div className="form-section-body">
            <div className="form-row form-row-3">
              <div className="form-group">
                <label className="form-label form-label-required">
                  Address
                </label>
                <input 
                  type="text" 
                  name="address"
                  value={propertyData.address}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="123 Main St"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  City, State ZIP
                </label>
                <input 
                  type="text" 
                  name="csz"
                  value={propertyData.csz}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Denver, CO 80202"
                />
                <p className="mt-1 text-text-light text-xs">
                  This will display below the address in the app
                </p>
              </div>
              
              <div className="form-group">
                <label className="form-label form-label-required">
                  Neighborhood
                </label>
                <select 
                  name="neighborhood"
                  value={propertyData.neighborhood}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                >
                  <option value="">Select a neighborhood</option>
                  {neighborhoods.map((neighborhood) => (
                    <option 
                      key={neighborhood._id} 
                      value={neighborhood._id}
                    >
                      {neighborhood.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="form-row form-row-4">
              <div className="form-group">
                <label className="form-label form-label-required">
                  Price
                </label>
                <input 
                  type="text" 
                  name="price"
                  value={propertyData.price}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="$725,000"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  Listing Type
                </label>
                <select 
                  name="listingType"
                  value={propertyData.listingType}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  <option value="purchase">Purchase</option>
                  <option value="rent">Rent</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label form-label-required">
                  Property Type
                </label>
                <select 
                  name="propertyType"
                  value={propertyData.propertyType}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                >
                  <option value="House">House</option>
                  <option value="Condo">Condo</option>
                  <option value="Apartment">Apartment</option>
                  <option value="Townhouse">Townhouse</option>
                  <option value="Multifamily">Multifamily</option>
                  <option value="ManufacturedMobile">Manufactured/Mobile</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  Property Status
                </label>
                <select 
                  name="propertyStatus"
                  value={propertyData.propertyStatus}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  <option value="For Sale">For Sale</option>
                  <option value="Pending">Pending</option>
                  <option value="Sold">Sold</option>
                  <option value="Off Market">Off Market</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  🔥 Trending
                </label>
                <div className="toggle-switch-container" style={{ marginTop: '8px' }}>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="isTrending"
                      checked={propertyData.isTrending}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="form-row form-row-4">
              <div className="form-group">
                <label className="form-label form-label-required">
                  Bedrooms
                </label>
                <input 
                  type="number" 
                  name="bedrooms"
                  value={propertyData.bedrooms}
                  onChange={handleInputChange}
                  className="form-control"
                  min="0"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label form-label-required">
                  Bathrooms
                </label>
                <input 
                  type="number" 
                  name="bathrooms"
                  value={propertyData.bathrooms}
                  onChange={handleInputChange}
                  className="form-control"
                  step="0.5"
                  min="0"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label form-label-required">
                  Square Feet
                </label>
                <input 
                  type="number" 
                  name="sqft"
                  value={propertyData.sqft}
                  onChange={handleInputChange}
                  className="form-control"
                  min="0"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  Lot Size (acres)
                </label>
                <input 
                  type="number" 
                  name="lotSize"
                  value={propertyData.lotSize}
                  onChange={handleInputChange}
                  className="form-control"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Description
                </label>
                <textarea 
                  name="description"
                  value={propertyData.description}
                  onChange={handleInputChange}
                  className="form-control"
                  rows="4"
                  placeholder="Property description..."
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Features (comma-separated)
                </label>
                <input 
                  type="text" 
                  name="features"
                  value={propertyData.features}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Hardwood Floors, Granite Countertops, Pool"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div className="form-section">
          <div className="form-section-header">
            <h3 className="form-section-title">Additional Details</h3>
          </div>
          <div className="form-section-body">
            <div className="form-row form-row-4">
              <div className="form-group">
                <label className="form-label">
                  Year Built
                </label>
                <input 
                  type="number" 
                  name="yearBuilt"
                  value={propertyData.yearBuilt}
                  onChange={handleInputChange}
                  className="form-control"
                  min="1800"
                  max={new Date().getFullYear()}
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  Year Renovated
                </label>
                <input 
                  type="number" 
                  name="yearRenovated"
                  value={propertyData.yearRenovated}
                  onChange={handleInputChange}
                  className="form-control"
                  min="1800"
                  max={new Date().getFullYear()}
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  Days on Market
                </label>
                <input 
                  type="number" 
                  name="daysOnMarket"
                  value={propertyData.daysOnMarket}
                  onChange={handleInputChange}
                  className="form-control"
                  min="0"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  HOA Fees
                </label>
                <input 
                  type="number" 
                  name="hoaFees"
                  value={propertyData.hoaFees}
                  onChange={handleInputChange}
                  className="form-control"
                  min="0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="form-section">
          <div className="form-section-header">
            <h3 className="form-section-title">Location Coordinates</h3>
          </div>
          <div className="form-section-body">
            <div className="form-row form-row-2">
              <div className="form-group">
                <label className="form-label">
                  Latitude
                </label>
                <input 
                  type="number" 
                  name="latitude"
                  value={propertyData.latitude}
                  onChange={handleInputChange}
                  className="form-control"
                  step="any"
                  placeholder="39.7392"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  Longitude
                </label>
                <input 
                  type="number" 
                  name="longitude"
                  value={propertyData.longitude}
                  onChange={handleInputChange}
                  className="form-control"
                  step="any"
                  placeholder="-104.9903"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="form-actions">
          <button 
            type="button" 
            onClick={handleCancel}
            className="btn-secondary"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button 
            type="submit"
            className="btn-primary"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className="loading-spinner"></span>
                Saving...
              </>
            ) : 'Save Changes'}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default EditPropertyPage;